// frontend/src/utils/adherencia.ts
/**
 * Bandas de adherencia / rendimiento (producido vs. planificado de la OT).
 *
 * El objetivo de planta es producir ENTRE el 98 % y el 102 % de la OT: quedarse
 * corto y pasarse son igual de malos, así que la escala NO es «más es mejor»
 * sino una campana centrada en el 100 %.
 *
 *     < 97 %       rojo    desviación grave por defecto
 *   97 –  98 %     ámbar   aviso
 *   98 – 102 %     verde   objetivo
 *  102 – 105 %     ámbar   aviso (sobreproducción)
 *    > 105 %       rojo    desviación grave por exceso
 *
 * Criterio ÚNICO para todas las pestañas que pintan un % de adherencia
 * (Secuencia, Producción diaria, …). Los umbrales se cambian aquí y en ningún
 * otro sitio.
 */

/** Franja verde: el objetivo. */
export const ADHERENCE_TARGET = { min: 98, max: 102 } as const
/** Franja ámbar: fuera de objetivo pero tolerable. */
export const ADHERENCE_WARN = { min: 97, max: 105 } as const

export type AdherenceBand = 'green' | 'amber' | 'red'

/** Banda de un porcentaje; `null` cuando no hay dato (sin planificado). */
export function adherenceBand(pct: number | null | undefined): AdherenceBand | null {
  if (pct == null || !Number.isFinite(pct)) return null
  if (pct < ADHERENCE_WARN.min) return 'red'
  if (pct < ADHERENCE_TARGET.min) return 'amber'
  if (pct <= ADHERENCE_TARGET.max) return 'green'
  if (pct <= ADHERENCE_WARN.max) return 'amber'
  return 'red'
}

/** ¿Fuera del objetivo 98–102 %? `false` cuando no hay dato. */
export function isAdherenceOffTarget(pct: number | null | undefined): boolean {
  const band = adherenceBand(pct)
  return band !== null && band !== 'green'
}

// ── Clases Tailwind por banda ────────────────────────────────────────────────
// Literales completos en tablas, nunca concatenados: Tailwind escanea el fuente
// y no ve las clases construidas con plantillas.

const TEXT: Record<AdherenceBand, { dark: string; light: string }> = {
  green: { dark: 'text-emerald-300', light: 'text-emerald-700' },
  amber: { dark: 'text-amber-300',   light: 'text-amber-700'   },
  red:   { dark: 'text-red-300',     light: 'text-red-600'     },
}

const BG: Record<AdherenceBand, { dark: string; light: string }> = {
  green: { dark: 'bg-emerald-500/15', light: 'bg-emerald-50' },
  amber: { dark: 'bg-amber-500/15',   light: 'bg-amber-50'   },
  red:   { dark: 'bg-red-500/20',     light: 'bg-red-50'     },
}

const SOLID: Record<AdherenceBand, { dark: string; light: string }> = {
  green: { dark: 'bg-emerald-500', light: 'bg-emerald-400' },
  amber: { dark: 'bg-amber-500',   light: 'bg-amber-400'   },
  red:   { dark: 'bg-red-500',     light: 'bg-red-400'     },
}

const ACCENT: Record<AdherenceBand, 'emerald' | 'amber' | 'red'> = {
  green: 'emerald', amber: 'amber', red: 'red',
}

/** Color de texto del %. Cadena vacía si no hay dato. */
export function adherenceTextCls(pct: number | null | undefined, dark: boolean): string {
  const band = adherenceBand(pct)
  return band ? (dark ? TEXT[band].dark : TEXT[band].light) : ''
}

/** Fondo de la celda del %. Cadena vacía si no hay dato. */
export function adherenceBgCls(pct: number | null | undefined, dark: boolean): string {
  const band = adherenceBand(pct)
  return band ? (dark ? BG[band].dark : BG[band].light) : ''
}

/** Relleno sólido para barras de progreso. Cadena vacía si no hay dato. */
export function adherenceBarCls(pct: number | null | undefined, dark: boolean): string {
  const band = adherenceBand(pct)
  return band ? (dark ? SOLID[band].dark : SOLID[band].light) : ''
}

/** Acento de `<KpiCard valueAccent>`. `undefined` si no hay dato. */
export function adherenceAccent(
  pct: number | null | undefined,
): 'emerald' | 'amber' | 'red' | undefined {
  const band = adherenceBand(pct)
  return band ? ACCENT[band] : undefined
}
